import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
  CheckCircle as UnblockIcon,
  CurrencyRupee as RupeeIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  PhoneAndroid as RechargeIcon,
  AccountBalanceWallet as WalletIcon,
  Favorite as MilanIcon,
  TrendingUp as GrowIcon,
  TempleHindu as YatraIcon,
  ShoppingCart as ProductIcon,
  Refresh as RefreshIcon,
  FileDownload as DownloadIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";

const api_url = process.env.REACT_APP_BASE_URL || "https://api.bharatdarshan.app/";

const SERVICE_OPTIONS = [
  { value: "all", label: "All Services" },
  { value: "recharge", label: "Recharge" },
  { value: "flight", label: "Flight" },
  { value: "hotel", label: "Hotel" },
  { value: "wallet", label: "Wallet" },
  { value: "milansathi", label: "Milan Sathi" },
  { value: "grow", label: "Grow" },
  { value: "service", label: "Services (Sanatani/Kutumb)" },
  { value: "yatra", label: "Yatra" },
  { value: "sanatani_hotel", label: "Dharamshala" },
  { value: "product_order", label: "Product Orders" },
  { value: "pooja", label: "Pooja" },
  { value: "transaction", label: "Payments (Flight/Hotel)" },
  { value: "booking_attempt", label: "Booking Attempts" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "success", label: "Success" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

const getStatusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (["success", "completed", "confirmed", "ticketed", "paid", "active", "booking_success"].includes(s)) return "success";
  if (["pending", "processing", "initiated", "started", "payment_initiated"].includes(s)) return "warning";
  if (["failed", "failure", "inactive", "payment_failed", "booking_failed"].includes(s)) return "error";
  if (["cancelled", "refunded", "cancel", "payment_cancelled"].includes(s)) return "default";
  return "info";
};

const getServiceColor = (type) => {
  const colors = {
    Recharge: "#2196F3",
    Flight: "#9C27B0",
    Hotel: "#FF9800",
    Wallet: "#4CAF50",
    "Milan Sathi": "#E91E63",
    Grow: "#00BCD4",
    Service: "#795548",
    Yatra: "#FF5722",
    Dharamshala: "#607D8B",
    "Product Order": "#3F51B5",
    Pooja: "#F44336",
    Payment: "#8E24AA",
    "Booking Attempt": "#D84315",
  };
  return colors[type] || "#757575";
};

const formatDate = (d) => {
  if (!d) return "-";
  const date = new Date(d);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (a) => {
  const num = parseFloat(a);
  if (isNaN(num)) return "₹0";
  return "₹" + num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

export default function UnifiedTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({});
  const [serviceBreakdown, setServiceBreakdown] = useState({});
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockUser, setBlockUser] = useState(null);
  const [blockLoading, setBlockLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        serviceType: serviceFilter,
      });
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (search) params.append("search", search);
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo) params.append("dateTo", dateTo);

      const res = await fetch(`${api_url}api/admins/unified-transactions?${params}`);
      const data = await res.json();
      if (data.success) {
        setTransactions(data.data || []);
        setTotalCount(data.pagination?.total || 0);
        setStats(data.stats || {});
        setServiceBreakdown(data.serviceBreakdown || {});
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setSnackbar({ open: true, message: "Failed to fetch transactions", severity: "error" });
    }
    setLoading(false);
  }, [page, rowsPerPage, serviceFilter, statusFilter, search, dateFrom, dateTo]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(0);
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams({
        page: 1,
        limit: 10000,
        serviceType: serviceFilter,
      });
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (search) params.append("search", search);
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo) params.append("dateTo", dateTo);
      setSnackbar({ open: true, message: "Preparing Excel...", severity: "info" });
      const res = await fetch(`${api_url}api/admins/unified-transactions?${params}`);
      const data = await res.json();
      const rows = (data && data.data) || [];
      if (!rows.length) {
        setSnackbar({ open: true, message: "No data to export", severity: "warning" });
        return;
      }
      const exportRows = rows.map((txn, idx) => ({
        "#": idx + 1,
        "Real User": txn.userName || "Unknown",
        "Email": txn.userEmail || "",
        "Phone": txn.userPhone || "",
        "Dummy User": txn.dummyUserName || "",
        "Dummy Phone": txn.dummyUserPhone || "",
        "Service": txn.serviceType || "",
        "Sub Service": txn.serviceSubType || "",
        "Description": txn.description || "",
        "Amount": txn.amount || 0,
        "Payment Method": txn.paymentMethod || "",
        "Status": txn.status || "",
        "Failure Reason": txn.failureReason || "",
        "Date": txn.date ? new Date(txn.date).toLocaleString("en-IN") : "",
        "Transaction ID": txn.transactionId || txn._id || "",
        "User ID": txn.userId || "",
      }));
      const ws = XLSX.utils.json_to_sheet(exportRows);
      ws["!cols"] = Object.keys(exportRows[0]).map((k) => ({ wch: Math.min(30, Math.max(10, k.length + 2)) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Transactions");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      XLSX.writeFile(wb, `transactions_${ts}.xlsx`);
      setSnackbar({ open: true, message: `Exported ${exportRows.length} rows`, severity: "success" });
    } catch (err) {
      console.error("Export error:", err);
      setSnackbar({ open: true, message: "Failed to export Excel", severity: "error" });
    }
  };

  const handleBlockUser = async () => {
    if (!blockUser) return;
    setBlockLoading(true);
    try {
      const res = await fetch(`${api_url}api/admins/users/${blockUser.userId}/block`, { method: "PUT" });
      const data = await res.json();
      if (data.success) {
        const action = data.data?.status === "suspended" ? "blocked" : "unblocked";
        setSnackbar({ open: true, message: `User ${action} successfully`, severity: "success" });
        fetchTransactions();
      } else {
        setSnackbar({ open: true, message: data.message || "Failed", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Error blocking user", severity: "error" });
    }
    setBlockLoading(false);
    setBlockDialogOpen(false);
    setBlockUser(null);
  };

  // Stats cards
  const statsCards = [
    { label: "Total", value: stats.total || 0, color: "#2196F3", filter: "all" },
    { label: "Success", value: stats.success || 0, color: "#4CAF50", filter: "success" },
    { label: "Pending", value: stats.pending || 0, color: "#FF9800", filter: "pending" },
    { label: "Failed", value: stats.failed || 0, color: "#F44336", filter: "failed" },
    { label: "Cancelled", value: stats.cancelled || 0, color: "#9E9E9E", filter: "cancelled" },
    { label: "Revenue", value: formatAmount(stats.totalRevenue || 0), color: "#4CAF50", isRevenue: true },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          All Transactions
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
          >
            Export Excel
          </Button>
          <IconButton onClick={fetchTransactions} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statsCards.map((card) => (
          <Grid item xs={6} sm={4} md={2} key={card.label}>
            <Card
              sx={{
                cursor: card.isRevenue ? "default" : "pointer",
                borderTop: `4px solid ${card.color}`,
                "&:hover": card.isRevenue ? {} : { boxShadow: 6 },
                bgcolor: !card.isRevenue && statusFilter === card.filter ? `${card.color}15` : "white",
              }}
              onClick={() => !card.isRevenue && setStatusFilter(card.filter === statusFilter ? "all" : card.filter)}
            >
              <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                <Typography variant="h5" fontWeight="bold" color={card.color}>
                  {card.isRevenue ? card.value : card.value.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Service Breakdown Chips */}
      {Object.keys(serviceBreakdown).length > 0 && (
        <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {Object.entries(serviceBreakdown).map(([svc, info]) => (
            <Chip
              key={svc}
              label={`${svc}: ${info.count} (${formatAmount(info.revenue)})`}
              size="small"
              sx={{ bgcolor: `${getServiceColor(svc)}20`, color: getServiceColor(svc), fontWeight: 500 }}
            />
          ))}
        </Box>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search name, phone, dummy user..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Service</InputLabel>
              <Select
                value={serviceFilter}
                label="Service"
                onChange={(e) => { setServiceFilter(e.target.value); setPage(0); }}
              >
                {SERVICE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={2} md={1}>
            <Button variant="contained" size="small" fullWidth onClick={handleSearch}>
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer sx={{ maxHeight: "65vh" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>#</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>Real User</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>Dummy User</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>Service</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>Description</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }} align="right">Amount</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>Gateway</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No transactions found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((txn, idx) => {
                  const isBlocked = txn.userStatus === "suspended";
                  return (
                    <TableRow
                      key={txn._id || idx}
                      hover
                      sx={{
                        bgcolor: isBlocked ? "#FFEBEE" : "inherit",
                        "&:hover": { bgcolor: isBlocked ? "#FFCDD2" : undefined },
                      }}
                    >
                      <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 130 }}>
                            {txn.userName || "Unknown"}
                          </Typography>
                          {isBlocked && <Chip label="Blocked" size="small" color="error" sx={{ height: 18, fontSize: 10 }} />}
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 130, display: "block" }}>
                          {txn.userEmail || ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{txn.userPhone || "-"}</Typography>
                      </TableCell>
                      <TableCell>
                        {txn.dummyUserName ? (
                          <Box>
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 120, color: "#9C27B0" }}>
                              {txn.dummyUserName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 120, display: "block" }}>
                              {txn.dummyUserPhone || ""}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={txn.serviceType}
                          size="small"
                          sx={{
                            bgcolor: `${getServiceColor(txn.serviceType)}20`,
                            color: getServiceColor(txn.serviceType),
                            fontWeight: 600,
                            fontSize: 11,
                          }}
                        />
                        {txn.serviceSubType && (
                          <Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ maxWidth: 120, mt: 0.3 }}>
                            {txn.serviceSubType}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }} title={txn.description}>
                          {txn.description || "-"}
                        </Typography>
                        {txn.failureReason && (
                          <Typography variant="caption" color="error" noWrap sx={{ maxWidth: 200, display: "block" }}>
                            {txn.failureReason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatAmount(txn.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                          {txn.paymentMethod || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={txn.status}
                          size="small"
                          color={getStatusColor(txn.status)}
                          sx={{ fontSize: 11, fontWeight: 500, textTransform: "capitalize" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{formatDate(txn.date)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => { setSelectedTxn(txn); setViewOpen(true); }}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {txn.userId && (
                            <Tooltip title={isBlocked ? "Unblock User" : "Block User"}>
                              <IconButton
                                size="small"
                                color={isBlocked ? "success" : "error"}
                                onClick={() => { setBlockUser(txn); setBlockDialogOpen(true); }}
                              >
                                {isBlocked ? <UnblockIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* View Dialog */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#f5f5f5" }}>
          Transaction Details
          {selectedTxn && (
            <Chip
              label={selectedTxn.serviceType}
              size="small"
              sx={{ ml: 1, bgcolor: `${getServiceColor(selectedTxn.serviceType)}20`, color: getServiceColor(selectedTxn.serviceType) }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedTxn && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>Real User</Typography>
              <DetailRow label="Name" value={selectedTxn.userName} />
              <DetailRow label="Phone" value={selectedTxn.userPhone} />
              <DetailRow label="Email" value={selectedTxn.userEmail} />
              <DetailRow label="User Status" value={selectedTxn.userStatus === "suspended" ? "BLOCKED" : selectedTxn.userStatus} color={selectedTxn.userStatus === "suspended" ? "red" : "green"} />
              {selectedTxn.dummyUserName && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1, color: "#9C27B0" }}>Dummy User (PayU)</Typography>
                  <DetailRow label="Name" value={selectedTxn.dummyUserName} color="#9C27B0" />
                  <DetailRow label="Phone" value={selectedTxn.dummyUserPhone} color="#9C27B0" />
                  <DetailRow label="Email" value={selectedTxn.dummyUserEmail} color="#9C27B0" />
                </>
              )}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>Transaction</Typography>
              <DetailRow label="Service" value={`${selectedTxn.serviceType} - ${selectedTxn.serviceSubType || ""}`} />
              <DetailRow label="Description" value={selectedTxn.description} />
              <DetailRow label="Amount" value={formatAmount(selectedTxn.amount)} bold />
              <DetailRow label="Payment Method" value={selectedTxn.paymentMethod} />
              <DetailRow label="Status" value={selectedTxn.status} color={getStatusColor(selectedTxn.status) === "success" ? "green" : getStatusColor(selectedTxn.status) === "error" ? "red" : "orange"} />
              {selectedTxn.failureReason && <DetailRow label="Failure Reason" value={selectedTxn.failureReason} color="red" />}
              {selectedTxn.transactionId && <DetailRow label="Transaction ID" value={selectedTxn.transactionId} />}
              {selectedTxn.razorpayOrderId && <DetailRow label="Razorpay Order" value={selectedTxn.razorpayOrderId} />}
              <DetailRow label="Date" value={formatDate(selectedTxn.date)} />
              <DetailRow label="Record ID" value={selectedTxn._id} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedTxn?.userId && (
            <Button
              color={selectedTxn.userStatus === "suspended" ? "success" : "error"}
              onClick={() => { setViewOpen(false); setBlockUser(selectedTxn); setBlockDialogOpen(true); }}
            >
              {selectedTxn.userStatus === "suspended" ? "Unblock User" : "Block User"}
            </Button>
          )}
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Block Confirmation Dialog */}
      <Dialog open={blockDialogOpen} onClose={() => !blockLoading && setBlockDialogOpen(false)}>
        <DialogTitle>
          {blockUser?.userStatus === "suspended" ? "Unblock User" : "Block User"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {blockUser?.userStatus === "suspended" ? "unblock" : "block"}{" "}
            <strong>{blockUser?.userName}</strong> ({blockUser?.userPhone})?
          </Typography>
          {blockUser?.userStatus !== "suspended" && (
            <Typography color="error" sx={{ mt: 1 }} variant="body2">
              This will prevent the user from accessing the app.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)} disabled={blockLoading}>Cancel</Button>
          <Button
            variant="contained"
            color={blockUser?.userStatus === "suspended" ? "success" : "error"}
            onClick={handleBlockUser}
            disabled={blockLoading}
          >
            {blockLoading ? <CircularProgress size={20} /> : blockUser?.userStatus === "suspended" ? "Unblock" : "Block"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function DetailRow({ label, value, color, bold }) {
  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ color: color || "inherit", fontWeight: bold ? 700 : 400, wordBreak: "break-all" }}>
        {value || "-"}
      </Typography>
    </Box>
  );
}
